import {
  computed, inject, provide, reactive,
} from 'vue';

export enum HealthState {
  // Not sick
  Healthy,
  // Less than 2 years sick
  ShortSick,
  // More than 2 years sick
  LongSick,
}

export interface InsuranceState {
  hasWIAEmployee: boolean;
  hasWIAExcedent: boolean;
  hasWIAExcedentTop: boolean;
}

export interface CalculationState {
  age: number;
  situation: HealthState | null;
  salary: number;
  // ratio of disability, range 0-1
  disabilityRatio: number;
  extraIncome: number;
  cannotRecover: boolean;

  insurance: InsuranceState;
}

export const CalculationStateSymbol = Symbol('global calculation state');

export function createStore(): CalculationState {
  return reactive<CalculationState>({
    age: 41,
    situation: null,
    salary: 22000,
    disabilityRatio: 0.5,
    extraIncome: 0,
    cannotRecover: false,

    insurance: {
      hasWIAEmployee: true,
      hasWIAExcedent: false,
      hasWIAExcedentTop: false,
    },
  });
}

export function provideCalculationState(): CalculationState {
  const store = createStore();
  provide(CalculationStateSymbol, store);
  return store;
}

export function useCalculationState() {
  return inject(CalculationStateSymbol) as CalculationState;
}

export function useCalculations() {
  const state = useCalculationState();

  const disabilityPercent = computed({
    get: () => Math.round(state.disabilityRatio * 100),
    set: (val) => {
      state.disabilityRatio = val / 100;
    },
  });
  const grossMonthlySalary = computed(() => Math.round(state.salary / 12));
  const hasHighDisability = computed(() => state.disabilityRatio >= 0.8);
  const maxedExtraIncome = computed(() => Math.min(state.salary, state.extraIncome));
  const extraMonthlyIncome = computed({
    get() {
      return Math.round(maxedExtraIncome.value / 12);
    },
    set(value: number) {
      state.extraIncome = Math.min(state.salary, Math.round(value * 12));
    },
  });

  return {
    state,

    disabilityPercent,
    grossMonthlySalary,
    extraMonthlyIncome,

    hasHighDisability,
  };
}
